<div
  class="w-full max-w-5xl bg-surface-0 dark:bg-surface-900 rounded-3xl shadow overflow-hidden grid grid-cols-1 lg:grid-cols-5"
>
  <aside
    class="lg:col-span-2 bg-primary-50 dark:bg-primary-950 p-8 md:p-10 flex flex-col gap-6"
    aria-labelledby="trialHeading"
  >
    <img src="assets/layout/images/logo.svg" [alt]="'auth.register.logoAlt' | translate" class="w-[64px]" />

    <span class="self-start inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-sm font-semibold">
      <i class="pi pi-gift" aria-hidden="true"></i>
      {{ 'auth.register.trialBadge' | translate }}
    </span>

    <div>
      <h1 id="trialHeading" class="text-surface-900 dark:text-surface-0 text-3xl font-semibold mb-3 mt-0">
        {{ 'auth.register.trialHeading' | translate }}
      </h1>
      <p class="text-muted-color m-0 leading-relaxed">{{ 'auth.register.trialDescription' | translate }}</p>
    </div>

    <ul class="list-none p-0 m-0 flex flex-col gap-4">
      @for (benefit of trialBenefits; track benefit.title) {
        <li class="flex items-start gap-3">
          <span
            class="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-surface-0 dark:bg-surface-900 text-primary"
            aria-hidden="true"
          >
            <i [class]="benefit.icon"></i>
          </span>
          <div class="flex flex-col gap-1 pt-1">
            <span class="font-semibold text-surface-900 dark:text-surface-0">{{ benefit.title | translate }}</span>
            <span class="text-sm text-surface-700 dark:text-surface-200 leading-relaxed">{{
              benefit.description | translate
            }}</span>
          </div>
        </li>
      }
    </ul>
  </aside>

  <section class="lg:col-span-3 p-8 md:p-10" aria-labelledby="registerHeading">
    <h2 id="registerHeading" class="text-surface-900 dark:text-surface-0 text-2xl font-semibold mb-1 mt-0">
      {{ 'auth.register.register' | translate }}
    </h2>
    <p class="text-muted-color mt-0 mb-6">{{ 'auth.register.description' | translate }}</p>

    <app-loader [name]="registerComponentLoader" [backgroundClass]="'rounded-xl'">
      <form [formGroup]="editForm" (ngSubmit)="onSubmit()" class="grid grid-cols-1 md:grid-cols-2 gap-4" novalidate>
        <div class="md:col-span-2">
          <label [appRequiredLabel]="editForm.controls.companyName" for="companyName" class="block font-bold mb-2">{{
            'auth.register.companyName' | translate
          }}</label>
          <input
            pInputText
            id="companyName"
            formControlName="companyName"
            type="text"
            autocomplete="organization"
            class="w-full"
          />
          <app-form-error [control]="editForm.controls.companyName" />
        </div>

        <div>
          <label [appRequiredLabel]="editForm.controls.firstName" for="firstName" class="block font-bold mb-2">{{
            'auth.register.name' | translate
          }}</label>
          <input
            pInputText
            id="firstName"
            formControlName="firstName"
            type="text"
            autocomplete="given-name"
            class="w-full"
          />
          <app-form-error [control]="editForm.controls.firstName" />
        </div>

        <div>
          <label [appRequiredLabel]="editForm.controls.lastName" for="lastName" class="block font-bold mb-2">{{
            'auth.register.lastName' | translate
          }}</label>
          <input
            pInputText
            id="lastName"
            formControlName="lastName"
            type="text"
            autocomplete="family-name"
            class="w-full"
          />
          <app-form-error [control]="editForm.controls.lastName" />
        </div>

        <div class="md:col-span-2">
          <label [appRequiredLabel]="editForm.controls.email" for="email" class="block font-bold mb-2">{{
            'auth.register.email' | translate
          }}</label>
          <input pInputText id="email" formControlName="email" type="email" autocomplete="email" class="w-full" />
          <app-form-error [control]="editForm.controls.email" />
        </div>

        <div>
          <label [appRequiredLabel]="editForm.controls.password" for="password" class="block font-bold mb-2">{{
            'auth.register.password' | translate
          }}</label>
          <p-password
            inputId="password"
            formControlName="password"
            autocomplete="new-password"
            [toggleMask]="true"
            [feedback]="false"
            [fluid]="true"
            [ngClass]="{ 'input-error': editForm.controls.password.invalid && editForm.controls.password.dirty }"
          />
          <app-form-error [control]="editForm.controls.password" />
        </div>

        <div>
          <label
            [appRequiredLabel]="editForm.controls.confirmPassword"
            for="confirmPassword"
            class="block font-bold mb-2"
            >{{ 'auth.register.confirmPassword' | translate }}</label
          >
          <p-password
            inputId="confirmPassword"
            formControlName="confirmPassword"
            autocomplete="new-password"
            [toggleMask]="true"
            [feedback]="false"
            [fluid]="true"
            [ngClass]="{
              'input-error': editForm.controls.confirmPassword.invalid && editForm.controls.confirmPassword.dirty,
            }"
          />
          <app-form-error [control]="editForm.controls.confirmPassword" />
        </div>

        <div class="md:col-span-2" role="group" aria-labelledby="companyLogoLabel">
          <span id="companyLogoLabel" class="block font-bold mb-2">{{ 'auth.register.companyLogo' | translate }}</span>
          <p class="text-muted-color text-sm leading-relaxed mt-0 mb-3">
            {{ 'auth.register.companyLogoDescription' | translate }}
          </p>
          <div class="w-40">
            <app-image-form-control formControlName="imageId" header="auth.register.companyLogo" />
          </div>
        </div>

        <div class="md:col-span-2 flex flex-col gap-4 mt-4">
          <p-button
            [label]="'auth.register.startTrial' | translate"
            icon="pi pi-arrow-right"
            iconPos="right"
            size="large"
            styleClass="w-full"
            type="submit"
          />
          <p class="text-center text-muted-color m-0">
            {{ 'auth.register.alreadyHaveAccount' | translate }}
            <a class="font-semibold text-primary no-underline hover:underline" [routerLink]="['/auth/login']">{{
              'auth.register.backToLogin' | translate
            }}</a>
          </p>
        </div>
      </form>
    </app-loader>
  </section>
</div>
